import { useEffect, useRef } from 'react';

import { getLocalStorageItem, removeLocalStorageItem } from '@revenuemarket/browser';
import { Button, GrayColors, typographyStyle, Input, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { useQueryParam } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { useIsMobile } from '@revenuemarket/utils';
import { Divider, Form, InputRef, Typography } from 'antd';
import { Rule } from 'antd/es/form';
import { useRouter } from 'next/router';
import * as yup from 'yup';

import KakaoLoginButton from '~components/Auth/Signin/KakaoLoginButton';
import NaverLoginButton from '~components/Auth/Signin/NaverLoginButton';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useSignIn } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { AuthModel } from '~models/auth';
import { mixpanel } from '~pages/_app';

import GoogleOauthButton from './GoogleLoginButton';
import { useHandleLoginSuccess } from './callbacks';

const LoginScheme = yup.object({
  email: COMMON_YUP.email,
  password: COMMON_YUP.legacyPassword,
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await LoginScheme.validateAt(field, { [field]: value });
  },
});

export default function SigninForm() {
  const router = useRouter();
  const inputRef = useRef<InputRef | null>(null);
  const callbackUrl = useQueryParam('callbackUrl');

  const { handleLoginSuccess, contextHolder } = useHandleLoginSuccess();
  const [form] = Form.useForm<AuthModel.SignInRequest>();

  const { mutateAsync: signIn, isLoading } = useSignIn(callbackUrl);

  const isMobile = useIsMobile();

  const onFinish = async (values: AuthModel.SignInRequest) => {
    const mobileInvitationToken = getLocalStorageItem<string>('mobileInvitationToken');
    if (mobileInvitationToken) {
      removeLocalStorageItem('mobileInvitationToken');
    }

    mixpanel.track(MIXPANEL_EVENT.USER.CLICK_SIGNIN, {
      type: 'email',
    });

    const payload = {
      ...values,
      isMobile,
      mobileInvitationToken: mobileInvitationToken ?? undefined,
    };

    const response = await signIn(payload);

    if (response?.ErrorCode === 'INVALID_LOGIN_INFO') {
      Toast.error('아이디 또는 비밀번호가 올바르지 않습니다');
      return;
    }

    if (response.Status === ResponseStatus.FAILURE) {
      Toast.error('로그인에 실패했습니다.');
      return;
    }

    const accessToken = response?.Data?.data?.accessToken;
    if (!accessToken) {
      Toast.error('로그인에 실패했습니다.');
      return;
    }

    await handleLoginSuccess({
      accessToken,
      callbackUrl,
      values,
    });
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      className={css`
        width: 400px;

        @media (max-width: 768px) {
          width: 100%;
        }
      `}
    >
      {contextHolder}
      <Flex direction="column" gap={12}>
        <KakaoLoginButton
          title="카카오 로그인"
          onKakaoOauthClick={() => {
            mixpanel.track(MIXPANEL_EVENT.USER.CLICK_SIGNIN, {
              type: 'kakao',
            });
          }}
        />

        <NaverLoginButton
          title="네이버 로그인"
          callbackUrl={callbackUrl}
          onNaverOauthClick={() => {
            mixpanel.track(MIXPANEL_EVENT.USER.CLICK_SIGNIN, {
              type: 'naver',
            });
          }}
        />

        <GoogleOauthButton
          title="구글 로그인"
          callbackUrl={callbackUrl}
          onGoogleOauthClick={() => {
            mixpanel.track(MIXPANEL_EVENT.USER.CLICK_SIGNIN, {
              type: 'google',
            });
          }}
        />

        <Divider
          style={{
            ...typographyStyle['16_400'],
            color: GrayColors.gray800,
            margin: 0,
          }}
        >
          또는
        </Divider>

        <Flex direction="column" gap={8}>
          <Typography.Text
            style={{
              ...typographyStyle['16_400'],
              color: GrayColors.gray700,
              display: 'inline-block',
            }}
          >
            이메일 주소
          </Typography.Text>
          <Form.Item
            name="email"
            rules={[getSchemeRule('email')]}
            style={{
              margin: 0,
            }}
          >
            <Input
              inputSize="xlarge"
              type="email"
              placeholder="이메일을 입력해주세요"
              onChange={(e) => {
                form.setFields([
                  {
                    name: 'email',
                    errors: [],
                  },
                ]);
              }}
              ref={inputRef}
            />
          </Form.Item>
        </Flex>

        <Flex direction="column" gap={8}>
          <Typography.Text
            style={{
              ...typographyStyle['16_400'],
              color: GrayColors.gray700,
              display: 'inline-block',
            }}
          >
            비밀번호
          </Typography.Text>
          <div
            style={{
              position: 'relative',
            }}
          >
            <Form.Item
              name="password"
              rules={[getSchemeRule('password')]}
              style={{
                marginBottom: 32,
                position: 'relative',
              }}
              className={css`
                &&& .ant-form-item-explain-error {
                  height: 32px;
                  display: flex;
                  align-items: center;
                }

                &&& .ant-form-item-control-input {
                  margin-bottom: 4px;
                }
              `}
            >
              <Input
                inputSize="xlarge"
                passwordType={true}
                placeholder="비밀번호를 입력해주세요"
                onChange={(e) => {
                  form.setFields([
                    {
                      name: 'password',
                      errors: [],
                    },
                  ]);
                }}
              />
            </Form.Item>
            <Button
              buttonSize="default"
              buttonType="transparent"
              onClick={async () => {
                mixpanel.track(MIXPANEL_EVENT.USER.CLICK_FORGET_PASSWORD);
                await router.push('/auth/reset-password');
              }}
              style={{
                height: 32,
                lineHeight: 1,
                position: 'absolute',
                right: 0,
                bottom: 0,
              }}
            >
              비밀번호 찾기 →
            </Button>
          </div>
        </Flex>

        <Form.Item dependencies={['email', 'password']}>
          {({ getFieldsValue }) => {
            const valid = (() => {
              try {
                LoginScheme.validateSync(getFieldsValue());
                return true;
              } catch {
                return false;
              }
            })();

            return (
              <Flex direction="column">
                <Button
                  buttonSize="xlarge"
                  buttonType="primary"
                  type="submit"
                  loading={isLoading}
                  style={{
                    marginBottom: '12px',
                  }}
                >
                  이메일 로그인
                </Button>

                <Flex align="center" gap={8}>
                  <Typography.Text
                    style={{
                      ...typographyStyle['14_400'],
                      color: GrayColors.gray600,
                    }}
                  >
                    아직 계정이 없으신가요?
                  </Typography.Text>
                  <Button
                    buttonSize="default"
                    buttonType="transparent"
                    onClick={async () => {
                      await router.push('/auth/signup');
                    }}
                  >
                    가입하기
                  </Button>
                </Flex>
              </Flex>
            );
          }}
        </Form.Item>
      </Flex>
    </Form>
  );
}
